import {
  ActionType,
  prisma,
  ResourceType,
  UserRoleAssignmentEntityType,
  AbilityActionV2,
  AbilityResourceV2,
} from "client";

export const getPermissions = async () => {
  const permissions = await prisma.permission.findMany();
  return permissions;
};

type PermissionInfo = {
  action: ActionType;
  name: string;
  id: string;
};

export const getPermissionsGroupByResource = async () => {
  const permissions = await prisma.permission.findMany();
  const groupedPermissions = permissions.reduce(
    (acc: Record<ResourceType, PermissionInfo[]>, permission) => {
      const { resource, action, name, id } = permission;
      if (!acc[resource]) {
        acc[resource] = [];
      }
      acc[resource].push({ action, name, id });
      return acc;
    },
    {} as Record<ResourceType, PermissionInfo[]>
  );

  // Return the grouped permissions as an array of objects
  return Object.entries(groupedPermissions).map(([resource, actions]) => ({
    resource,
    permissions: actions,
  }));
};

export const getPermissionById = async (permissionId: string) => {
  const permission = await prisma.permission.findFirst({
    where: {
      id: permissionId,
    },
  });

  return permission;
};

export const getPermissionsByRoleId = async (roleId: string) => {
  const permissions = await prisma.permission.findMany({
    where: {
      roles: {
        some: {
          roleId: roleId,
        },
      },
    },
  });
  return permissions;
};

export const createPermission = async ({
  name,
  resource,
  action,
}: {
  name: string;
  resource: ResourceType;
  action: ActionType;
}) => {
  const createdPermission = await prisma.permission.create({
    data: {
      name,
      resource,
      action,
    },
  });

  return createdPermission;
};

export const createMultiplePermissions = async ({
  permissions,
}: {
  permissions: { name: string; resource: ResourceType; action: ActionType }[];
}) => {
  const createdPermissions = await prisma.permission.createMany({
    data: permissions.map(({ name, resource, action }) => ({
      name,
      resource,
      action,
    })),
  });

  return createdPermissions;
};

export const assignPermissionsToRole = async ({
  roleId,
  permissions,
}: {
  roleId: string;
  permissions: string[];
}) => {
  const role = await prisma.role.update({
    where: {
      id: roleId,
    },
    data: {
      permissions: {
        set: [],
        createMany: {
          data: permissions.map((permission) => ({
            permissionId: permission,
            roleId: roleId,
          })),
        },
      },
    },
  });
  return role;
};

export const userHasPermission = async (
  userId: string,
  permission: {
    resource: ResourceType;
    action: ActionType;
    assignment?: {
      type: UserRoleAssignmentEntityType;
      id: string;
    };
  }
): Promise<boolean> => {
  if (permission.assignment) {
    const userRoleAssignment = await prisma.assignmentModel.findMany({
      where: {
        userId,
        entityId: permission.assignment.id,
        entityType: permission.assignment.type,
      },
    });

    if (!userRoleAssignment) {
      return false;
    }
  }
  const userPermissions = await prisma.assignmentModel.findMany({
    where: {
      userId,
      OR: [
        {
          role: {
            permissions: {
              some: {
                permission: {
                  action: permission.action,
                  resource: permission.resource,
                },
              },
            },
          },
        },
        {
          permissions: {
            some: {
              action: permission.action,
              resource: permission.resource,
            },
          },
        },
      ],
    },
  });

  return userPermissions.length > 0;
};

/**
 * Get user with Role V2 data for access control
 * @param userId - User ID
 * @returns User with roleV2, profiles, organizations, and roles
 */
export const getUserWithRolesV2 = async (
  userId: string,
  whiteLabelId?: string
) => {
  const user = await prisma.user.findUnique({
    where: { id: userId },
    include: {
      roleV2: true,
      profiles: {
        where: {
          ...(whiteLabelId ? { event: { whiteLabelId } } : undefined),
          roleV2Id: { not: null },
        },
        include: {
          roleV2: true,
        },
      },
      organizations: true,
      userRoleAssignments: {
        include: {
          role: true,
        },
      },
    },
  });

  return user;
};

// /**
//  * Check if user has a specific ability based on Role V2
//  * @param userId - User ID to check
//  * @param resource - Resource to check access for
//  * @param action - Action to check (CREATE, READ, UPDATE, DELETE)
//  * @param context - Optional context (eventId, organizationId, whiteLabelId)
//  * @returns boolean indicating if user has the ability
//  */
// export const userHasAbilityV2 = async (
//   userId: string,
//   resource: AbilityResourceV2,
//   action: AbilityActionV2,
//   context?: {
//     eventId?: string;
//     organizationId?: string;
//     whiteLabelId?: string;
//   }
// ): Promise<boolean> => {
//   // Get user with Role V2 and profiles
//   const user = await prisma.user.findUnique({
//     where: { id: userId },
//     include: {
//       roleV2: {
//         include: {
//           abilities: true,
//         },
//       },
//       profiles: {
//         include: {
//           roleV2: {
//             include: {
//               abilities: true,
//             },
//           },
//         },
//       },
//       organizations: true,
//     },
//   });

//   if (!user) {
//     return false;
//   }

//   // Check user-level Role V2
//   if (user.roleV2) {
//     const hasAbility = user.roleV2.abilities.some(
//       (ability) => ability.resource === resource && ability.action === action
//     );

//     if (hasAbility) {
//       // For organization-scoped roles, verify user has access to the organization
//       if (context?.organizationId && user.organizations) {
//         const hasOrgAccess = user.organizations.some(
//           (org) => org.id === context.organizationId
//         );
//         if (!hasOrgAccess) {
//           return false;
//         }
//       }

//       // For white label-scoped roles, verify user has access to the white label
//       if (context?.whiteLabelId && user.accessibleWhiteLabelId) {
//         if (user.accessibleWhiteLabelId !== context.whiteLabelId) {
//           return false;
//         }
//       }

//       return true;
//     }
//   }

//   // Check profile-level Role V2 (event-specific)
//   if (context?.eventId && user.profiles) {
//     const eventProfile = user.profiles.find(
//       (profile) => profile.eventId === context.eventId
//     );

//     if (eventProfile?.roleV2) {
//       const hasAbility = eventProfile.roleV2.abilities.some(
//         (ability) => ability.resource === resource && ability.action === action
//       );

//       if (hasAbility) {
//         return true;
//       }
//     }
//   }

//   return false;
// };

// /**
//  * Get all abilities for a user based on Role V2
//  * @param userId - User ID
//  * @param eventId - Optional event ID to include profile-level abilities
//  * @returns Array of abilities with resource and action
//  */
// export const getUserAbilitiesV2 = async (
//   userId: string,
//   eventId?: string
// ): Promise<Array<{ resource: AbilityResourceV2; action: AbilityActionV2 }>> => {
//   const user = await prisma.user.findUnique({
//     where: { id: userId },
//     include: {
//       roleV2: {
//         include: {
//           abilities: true,
//         },
//       },
//       profiles: {
//         where: eventId ? { eventId } : undefined,
//         include: {
//           roleV2: {
//             include: {
//               abilities: true,
//             },
//           },
//         },
//       },
//     },
//   });

//   if (!user) {
//     return [];
//   }

//   const abilities: Array<{
//     resource: AbilityResourceV2;
//     action: AbilityActionV2;
//   }> = [];

//   // Add user-level abilities
//   if (user.roleV2?.abilities) {
//     user.roleV2.abilities.forEach((ability) => {
//       abilities.push({
//         resource: ability.resource,
//         action: ability.action,
//       });
//     });
//   }

//   // Add profile-level abilities
//   if (user.profiles) {
//     user.profiles.forEach((profile) => {
//       if (profile.roleV2?.abilities) {
//         profile.roleV2.abilities.forEach((ability) => {
//           abilities.push({
//             resource: ability.resource,
//             action: ability.action,
//           });
//         });
//       }
//     });
//   }

//   // Remove duplicates
//   return Array.from(
//     new Map(
//       abilities.map((ability) => [
//         `${ability.resource}-${ability.action}`,
//         ability,
//       ])
//     ).values()
//   );
// };
